/**
 * /settings/members — invite + manage workspace members. Cycle 17.
 *
 * Owner / admin only. Surfaces:
 *   - "Invite by email" form with role picker
 *   - Pending invites list with revoke
 *   - (Members list itself comes in a follow-up cycle alongside the
 *     /workspaces/me/members API surface)
 *
 * Active workspace id comes from /me/active-workspace, not a URL
 * param — same pattern as the rest of the dashboard.
 */
import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";

interface ActiveWorkspace {
  id: string;
  name: string;
  role?: string;
}

interface Invite {
  email: string;
  role: string;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  shortId: string;
}

const ROLES: ReadonlyArray<{ value: string; label: string; hint: string }> = [
  { value: "admin", label: "Admin", hint: "Settings + everything below" },
  { value: "editor", label: "Editor", hint: "Flows + broadcasts + sequences, no settings" },
  { value: "agent", label: "Agent", hint: "Inbox only — chat with contacts" },
  { value: "viewer", label: "Viewer", hint: "Read-only" },
];

export const Route = createFileRoute("/_app/settings/members")({
  component: Members,
});

function Members() {
  const [ws, setWs] = useState<ActiveWorkspace | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<string>("agent");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [autoAssignStrategy, setAutoAssignStrategy] = useState<
    "off" | "round_robin"
  >("off");

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setError(null);
    try {
      // Resolve active workspace + my role on it.
      const me = await api.get<{
        activeWorkspace: ActiveWorkspace;
        role: string | null;
      }>("/me");
      const active = { ...me.data.activeWorkspace, role: me.data.role ?? undefined };
      setWs(active);
      // Pull pending invites + workspace settings (for round-robin).
      const [inv, settings] = await Promise.all([
        api.get<{ invites: Invite[] }>(`/workspaces/${active.id}/invites`),
        api.get<{
          workspace: { autoAssignStrategy: "off" | "round_robin" };
        }>("/workspaces/me/settings"),
      ]);
      setInvites(inv.data.invites);
      setAutoAssignStrategy(
        settings.data.workspace?.autoAssignStrategy ?? "off",
      );
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 403) {
        setError("Only owner / admin can manage members.");
      } else {
        setError("Failed to load members.");
      }
    }
  }

  async function invite(e: FormEvent) {
    e.preventDefault();
    if (!ws || !email.trim() || busy) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await api.post(`/workspaces/${ws.id}/invites`, {
        email: email.trim(),
        role,
      });
      setInfo(`Invite sent to ${email.trim()}.`);
      setEmail("");
      await load();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response
        ?.data?.message;
      setError(msg ?? "Failed to send invite");
    } finally {
      setBusy(false);
    }
  }

  async function setStrategy(next: "off" | "round_robin") {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch("/workspaces/me/settings", {
        autoAssignStrategy: next,
      });
      setAutoAssignStrategy(next);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })
        .response?.data?.message;
      setError(msg ?? "Failed to update auto-assign");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(shortId: string, inviteEmail: string) {
    if (!ws) return;
    if (!confirm(`Revoke invite to ${inviteEmail}?`)) return;
    try {
      await api.delete(`/workspaces/${ws.id}/invites/${shortId}`);
      await load();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response
        ?.data?.message;
      setError(msg ?? "Failed to revoke");
    }
  }

  const pending = invites.filter((i) => !i.acceptedAt && !i.revokedAt);
  const past = invites.filter((i) => i.acceptedAt || i.revokedAt);

  return (
    <main style={{ maxWidth: 720, margin: "0 auto", padding: "32px 24px" }}>
      <h1 style={{ fontSize: 28, marginBottom: 4 }}>Members</h1>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 24 }}>
        Invite teammates to <strong>{ws?.name ?? "this workspace"}</strong>.
      </p>

      {error ? <div className="brut-error" style={{ marginBottom: 12 }}>{error}</div> : null}
      {info ? (
        <div
          className="brut-card"
          style={{ background: "#defbe5", padding: 10, marginBottom: 12 }}
        >
          {info}
        </div>
      ) : null}

      <div
        className="brut-card"
        style={{ padding: 14, display: "grid", gap: 10, marginBottom: 24 }}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>Auto-assign new conversations</div>
        <p style={{ margin: 0, color: "var(--muted)", fontSize: 13 }}>
          When ON, the first inbound on a brand-new conversation picks
          the next teammate in rotation (round-robin across owner /
          admin / editor / agent — viewer is read-only and skipped).
          The bot still engages normally.
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          {(["off", "round_robin"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => void setStrategy(v)}
              disabled={busy}
              className={
                autoAssignStrategy === v
                  ? "brut-button"
                  : "brut-button is-secondary"
              }
              style={{ fontSize: 13, padding: "6px 14px" }}
            >
              {v === "off" ? "Off" : "Round-robin"}
            </button>
          ))}
        </div>
      </div>

      <form
        onSubmit={invite}
        className="brut-card"
        style={{ padding: 14, display: "grid", gap: 10, marginBottom: 24 }}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>Invite by email</div>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="teammate@company.com"
          className="brut-input"
          required
        />
        <label className="brut-label">Role</label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="brut-input"
        >
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label} — {r.hint}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={busy || !email.trim()}
          className="brut-button"
        >
          {busy ? "Sending…" : "Send invite"}
        </button>
      </form>

      <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>
        Pending invites
      </h2>
      {pending.length === 0 ? (
        <div className="brut-card" style={{ padding: 10, marginBottom: 24 }}>
          No pending invites.
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, marginBottom: 24, display: "grid", gap: 8 }}>
          {pending.map((i) => (
            <li
              key={i.shortId}
              className="brut-card"
              style={{
                padding: 10,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 14 }}>
                <div>{i.email}</div>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>
                  {i.role} · expires {new Date(i.expiresAt).toLocaleDateString()}
                </div>
              </div>
              <button
                onClick={() => void revoke(i.shortId, i.email)}
                className="brut-button is-danger"
                style={{ fontSize: 12, padding: "4px 10px" }}
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}

      {past.length > 0 ? (
        <details>
          <summary style={{ fontSize: 14, color: "var(--muted)", cursor: "pointer" }}>
            Past invites ({past.length})
          </summary>
          <ul style={{ listStyle: "none", padding: 0, marginTop: 8, display: "grid", gap: 6 }}>
            {past.map((i) => (
              <li key={i.shortId} style={{ fontSize: 12, color: "var(--muted)" }}>
                {i.email} · {i.role} ·{" "}
                {i.acceptedAt
                  ? `accepted ${new Date(i.acceptedAt).toLocaleDateString()}`
                  : `revoked ${new Date(i.revokedAt!).toLocaleDateString()}`}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </main>
  );
}
